import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { BADGE_COLORS, Count } from '@/nova/components/Tab/Tab.styles';
import { COLORS } from '@/nova/theme/tokens';
import type { SortableRowsApi } from '../../../blog-editor/hooks/useSortableRows';
import * as S from './ListRow.styles';

/** What the row's grip is handed by the sorting hook (`useSortableRows`' handle). */
export type ListRowGrip = Partial<ReturnType<SortableRowsApi['handleProps']>>;

export type ListRowProps = {
  label: string;
  value: string;
  /** The open list: pressed, tinted and in the brand orange. */
  selected: boolean;
  /** Which place of its list this row is, when the page shows the list more than once (1, 2, ...): after the name, and in
   *  the names the row's controls are read by. */
  appearance?: number;
  count?: number;
  /** One of the owner's own lists: the lilac disc with the pencil before the label, and a lilac count. */
  own: boolean;
  /** The page hides the list: the crossed-out eye, muted words and count. */
  hidden: boolean;
  /** The list has changes not yet published: a dot before the count, explained in the row's tooltip. The page says
   *  so to screen readers. */
  unpublished?: boolean;
  /** Hides the list on the page, or shows it again. Without it (a list the page does not place, a page that cannot be
   *  arranged) the eye is held. */
  onToggleHidden?: () => void;
  /** The row can be dragged into a new order: its grip shows before its name, the sorting hook's handle (a finger drags
   *  it at once, the arrow keys move the row). */
  grip?: ListRowGrip;
  /** Opens the list. Receives `value` via `event.currentTarget.dataset.value`. */
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
};

/**
 * A game list's row in the Games tab's column of lists: the grip where it can be dragged, then the button that opens
 * it (its name, cut on one line with all of it in the tooltip, the own list's disc before the name, the unpublished dot
 * before the count, then the count), then the eye that hides it on the page or shows it again. The tooltip is the
 * browser's own: one drawn under the row would be cut off by the column, which scrolls.
 *
 * `hidden` is this component's prop: it is never spread onto an element, as the HTML `hidden` attribute would hide it.
 */
export const ListRow = ({
  label,
  value,
  selected,
  appearance,
  count,
  own,
  hidden,
  unpublished = false,
  onToggleHidden,
  grip,
  onClick,
}: ListRowProps): React.ReactElement => {
  const { t } = useTranslation();
  // A hidden list's count is muted. The owner's own lists carry no mark here (the user, 2026-10-21): the open list's
  // heading says "User list".
  const countColor = hidden ? COLORS.textFaint : BADGE_COLORS.muted.fg;
  // The name as the row shows it (an own list's as typed), numbered when the page shows the list more than once, then
  // what the dot means.
  const shown = own ? label : t(label);
  const name = appearance === undefined ? shown : `${shown} ${appearance}`;
  const title = unpublished ? `${name}\n${t('This list has unpublished changes.')}` : name;

  return (
    <S.Root $selected={selected} $hidden={hidden}>
      {grip ? (
        // A button, as the games' handle is: a finger near it lands on it rather than on the name beside it, and the
        // arrow keys move the row.
        <S.Grip type="button" aria-label={t('Reorder {{title}}', { title: name })} data-testid="drag-handle" {...grip}>
          <Icon name="drag-handle" size={16} />
        </S.Grip>
      ) : null}
      <S.Main
        type="button"
        data-value={value}
        data-row-main=""
        aria-pressed={selected}
        title={title}
        onClick={onClick}
      >
        {/* An own list's name is data, shown as typed (a name such as "Sports" is a catalogue key too). A site list's
            label goes through the translation, as it always has. */}
        <S.Label text={label} tag="span" raw={own} />
        {appearance !== undefined ? <S.Appearance>{appearance}</S.Appearance> : null}
        {unpublished ? <S.Dot aria-hidden="true" data-testid="unpublished-mark" /> : null}
        {count != null ? (
          <Count $bg={S.COUNT_BG} $fg={countColor}>
            {count}
          </Count>
        ) : null}
        {hidden ? <S.ScreenReaderText>{t('Hidden on your site')}</S.ScreenReaderText> : null}
      </S.Main>
      {/* Literal keys per branch, so the extractor harvests both. */}
      <S.Visibility
        type="button"
        aria-label={hidden ? t('Show {{name}}', { name }) : t('Hide {{name}}', { name })}
        title={hidden ? t('Show {{name}}', { name }) : t('Hide {{name}}', { name })}
        disabled={!onToggleHidden}
        onClick={onToggleHidden}
      >
        <Icon name={hidden ? 'eye-off' : 'eye'} size={16} />
      </S.Visibility>
    </S.Root>
  );
};

export default ListRow;
